'use client';

import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ViewerTeamIndicator } from '@/components/ViewerTeamIndicator';
import useFetchTeams from '@/hooks/useFetchTeams';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isOwner } from '@/lib/rbac';

// Select value for "the link assigns no team" (Base UI needs a non-empty value).
export const NO_TEAM = 'none';

interface TeamOption {
  TeamUUID: string;
  TeamName: string;
}

const toTeamOptions = (data: unknown): TeamOption[] =>
  Array.isArray(data)
    ? data.filter(
        (t): t is TeamOption =>
          typeof t === 'object' &&
          t !== null &&
          typeof (t as TeamOption).TeamUUID === 'string' &&
          typeof (t as TeamOption).TeamName === 'string'
      )
    : [];

/**
 * Which team the viewer's invite link is for. Links are keyed on
 * (role, team): an Owner picks the team (default: their own team, where
 * pre-existing links were backfilled), an Admin is fixed to their own team.
 *
 * @returns `teamKey` for query keys and the picker (`NO_TEAM` = no team),
 *   `teamUuid` for the API (undefined = no team, or not an Owner), and
 *   `setTeamKey`.
 */
export function useInviteLinkTeam() {
  const { user } = useUserLogged();
  const [picked, setPicked] = useState<string | null>(null);
  const ownTeam = user?.TeamUUID || NO_TEAM;
  const owner = isOwner(user?.Role);
  const teamKey = owner ? (picked ?? ownTeam) : ownTeam;
  return {
    teamKey,
    // Only an Owner sends a team: the backend ignores it for an Admin, and
    // require_org_access would refuse a stale one before that.
    teamUuid: owner && teamKey !== NO_TEAM ? teamKey : undefined,
    setTeamKey: setPicked,
  };
}

/**
 * Team picker for an Owner (org teams plus "No team"); a read-only
 * "Team: <name>" for an Admin, who can only invite into their own team.
 */
export function InviteLinkTeamControl({
  value,
  onChange,
  triggerClassName = 'w-40',
}: {
  value: string;
  onChange: (teamKey: string) => void;
  triggerClassName?: string;
}) {
  const { user } = useUserLogged();
  const { data, isLoading, error } = useFetchTeams();
  const teams = toTeamOptions(data);

  if (!isOwner(user?.Role)) {
    const ownTeam = user?.TeamUUID;
    if (!ownTeam)
      return <ViewerTeamIndicator teamName={null} isLoading={false} />;
    // Unreadable name: show nothing rather than claim the Admin has no team.
    const name = teams.find((t) => t.TeamUUID === ownTeam)?.TeamName ?? null;
    if (error || (!isLoading && !name)) return null;
    return <ViewerTeamIndicator teamName={name} isLoading={isLoading} />;
  }

  if (isLoading)
    return <Skeleton className="h-9 w-40 rounded-lg bg-ds-gray-subtlest" />;
  if (error) {
    return (
      <span className="ds-text-body-xs-medium text-ds-danger-default">
        Couldn&apos;t load teams
      </span>
    );
  }

  const items = [
    { value: NO_TEAM, label: 'No team' },
    ...teams.map((t) => ({ value: t.TeamUUID, label: t.TeamName })),
  ];
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (next != null) onChange(next);
      }}
      items={items}
    >
      <SelectTrigger className={triggerClassName} aria-label="Invite team">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
